/**
 * Change Password Page Component
 * Allows users to change their password (required on first login or optional later)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Message, Card } from '../components/ui';
import './ChangePassword.css';

const ChangePassword = ({ isRequired = false, onPasswordChanged }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (name === 'newPassword') {
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('One number');
    }

    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    if (type === 'error') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password
    if (!validatePassword(formData.newPassword)) {
      showMessage('Please meet all password requirements', 'error');
      return;
    }

    // Check passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      showMessage('New passwords do not match', 'error');
      return;
    }

    // Check that new password is different from current
    if (formData.currentPassword === formData.newPassword) {
      showMessage('New password must be different from current password', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Password changed successfully!', 'success');

        // Clear the requirePasswordChange flag from localStorage
        localStorage.removeItem('requirePasswordChange');

        // Notify parent component
        if (onPasswordChanged) {
          onPasswordChanged();
        }

        // Redirect after short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        showMessage(data.error || 'Failed to change password', 'error');
      }
    } catch (error) {
      showMessage('Network error: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rf-change-password-page">
      <div className="rf-center-container">
        <Card className="rf-change-password-card">
          <div className="rf-auth-logo">
            <i className="fas fa-key"></i>
            <span>{isRequired ? 'Set New Password' : 'Change Password'}</span>
          </div>

          {isRequired && (
            <div className="rf-password-notice">
              <i className="fas fa-info-circle"></i>
              <p>
                You must change your temporary password before continuing.
                Please create a secure password that you'll remember.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="rf-change-password-form">
            <Input
              label="Current Password"
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              required
              autoComplete="current-password"
              placeholder="Enter your current password"
            />

            <Input
              label="New Password"
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              placeholder="Enter your new password"
            />

            {formData.newPassword && (
              <div className="rf-password-requirements">
                <p className="rf-requirements-title">Password requirements:</p>
                <ul>
                  <li className={formData.newPassword.length >= 8 ? 'rf-met' : ''}>
                    <i className={`fas ${formData.newPassword.length >= 8 ? 'fa-check' : 'fa-times'}`}></i>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(formData.newPassword) ? 'rf-met' : ''}>
                    <i className={`fas ${/[A-Z]/.test(formData.newPassword) ? 'fa-check' : 'fa-times'}`}></i>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.newPassword) ? 'rf-met' : ''}>
                    <i className={`fas ${/[a-z]/.test(formData.newPassword) ? 'fa-check' : 'fa-times'}`}></i>
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(formData.newPassword) ? 'rf-met' : ''}>
                    <i className={`fas ${/[0-9]/.test(formData.newPassword) ? 'fa-check' : 'fa-times'}`}></i>
                    One number
                  </li>
                </ul>
              </div>
            )}

            <Input
              label="Confirm New Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              placeholder="Confirm your new password"
            />

            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="rf-password-mismatch">
                <i className="fas fa-exclamation-circle"></i>
                Passwords do not match
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              block
              disabled={isLoading || passwordErrors.length > 0 || formData.newPassword !== formData.confirmPassword}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Changing Password...
                </>
              ) : (
                <>
                  <i className="fas fa-lock"></i>
                  Change Password
                </>
              )}
            </Button>
          </form>

          {message.text && (
            <Message type={message.type}>{message.text}</Message>
          )}

          {!isRequired && (
            <div className="rf-change-password-footer">
              <button
                type="button"
                className="rf-link-button"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left"></i>
                Go Back
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
